import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';

const useStyles = makeStyles((theme) => ({
  toast: {
    position: 'fixed',
    bottom: 24,
    left: 24,
    padding: '12px 20px',
    borderRadius: 8,
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontWeight: 500,
    fontSize: '0.9rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    transform: 'translateY(100px)',
    opacity: 0,
    transition: 'all 0.3s ease',
    zIndex: 10000,
  },
  visible: {
    transform: 'translateY(0)',
    opacity: 1,
  },
  success: {
    backgroundColor: '#10b981',
    color: 'white',
  },
  error: {
    backgroundColor: '#ef4444',
    color: 'white',
  },
  info: {
    backgroundColor: '#3b82f6',
    color: 'white',
  },
  icon: {
    fontSize: 20,
  },
}));

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  visible: boolean;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type = 'success', duration = 3000, visible, onClose }) => {
  const classes = useStyles();

  useEffect(() => {
    if (visible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [visible, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className={classes.icon} />;
      case 'error':
        return <ErrorIcon className={classes.icon} />;
      case 'info':
        return <InfoIcon className={classes.icon} />;
      default:
        return null;
    }
  };

  return (
    <div className={`${classes.toast} ${classes[type]} ${visible ? classes.visible : ''}`}>
      {getIcon()}
      {message}
    </div>
  );
};

export default Toast;

// Hook for easy toast usage
export function useToast() {
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: ToastType;
  }>({
    visible: false,
    message: '',
    type: 'success',
  });

  const showToast = (message: string, type: ToastType = 'success') => {
    setToast({ visible: true, message, type });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  return { toast, showToast, hideToast };
}
